import { StandardEvents } from '@shopify/events';

export function streamKey(value) {
  const url = new URL(value); url.hash = ''; url.searchParams.delete('section_id');
  return url.pathname + url.search;
}
export function nextStreamPage(loaded, pages, direction) {
  if (!loaded.size) return null;
  const page = direction === 'previous' ? Math.min(...loaded) - 1 : Math.max(...loaded) + 1;
  return page >= 1 && page <= pages ? page : null;
}
// Only fetched native card roots are renamed; native form/input IDs stay intact.
export function namespaceIncomingCards(cards, sectionId, document) {
  const reserved = new Set(cards.flatMap(card => [card, ...card.querySelectorAll('*')]).map(node => node.id).filter(Boolean));
  const references = ['for', 'form', 'aria-labelledby', 'aria-describedby', 'aria-controls', 'aria-owns', 'aria-activedescendant', 'aria-details', 'aria-errormessage'];
  for (const card of cards) {
    const renamed = new Map();
    const roots = [...card.querySelectorAll('product-card[id]')];
    roots.forEach((root, index) => {
      const oldId = root.id;
      const base = `s24-stream-${encodeURIComponent(sectionId)}-${encodeURIComponent(card.dataset.productId)}-card-${index}`;
      let id = base, suffix = 0;
      while (reserved.has(id) || document.getElementById(id)) id = `${base}-${++suffix}`;
      reserved.add(id); renamed.set(oldId, id); root.id = id;
    });
    if (!renamed.size) continue;
    for (const node of [card, ...card.querySelectorAll('*')]) {
      for (const attribute of references) {
        const value = node.getAttribute(attribute);
        if (value == null) continue;
        const next = value.replace(/\S+/g, id => renamed.get(id) || id);
        if (next !== value) node.setAttribute(attribute, next);
      }
      for (const attribute of ['href', 'xlink:href']) {
        const value = node.getAttribute(attribute);
        if (value?.startsWith('#') && renamed.has(value.slice(1))) node.setAttribute(attribute, `#${renamed.get(value.slice(1))}`);
      }
    }
  }
}
class CollectionStream extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.addEventListener('click', event => { if (event.target.closest('[data-stream-retry]')) this.retry(); }, options);
    this.addEventListener(StandardEvents.collectionUpdate, event => this.filterUpdate(event), options);
    window.addEventListener('pagehide', () => this.remember(true), options);
    window.addEventListener('pageshow', event => { if (event.persisted) this.pump(); }, options);
    window.addEventListener('popstate', () => { this.cancel(); window.location.reload(); }, options);
    if (typeof IntersectionObserver !== 'function') {
      const onScroll = () => { if (this.scrollFrame) return; this.scrollFrame = requestAnimationFrame(() => { this.scrollFrame = null; this.pump(); }); };
      window.addEventListener('scroll', onScroll, { ...options, capture: true, passive: true });
      window.addEventListener('resize', onScroll, { ...options, passive: true });
    }
    this.mutations = new MutationObserver(() => {
      if (this.filterPending || !this.isConnected) return;
      const grid = this.querySelector('[ref="grid"]'), meta = this.querySelector('[data-stream-meta]');
      if (grid !== this.grid || meta !== this.meta || this.nodes?.some(node => !grid?.contains(node))) this.reset();
    });
    this.mutations.observe(this, { childList: true, subtree: true });
    this.reset();
  }
  disconnectedCallback() {
    this.cancel(); this.mutations?.disconnect(); this.controller?.abort(); this.controller = null;
  }
  cancel() {
    this.generation = (this.generation || 0) + 1;
    this.request?.abort(); this.request = null; this.busy = false; this.removeAttribute('aria-busy');
    this.intersections?.disconnect();
  }
  reset() {
    this.cancel(); this.filterPending = false; this.error = null;
    this.grid = this.querySelector('[ref="grid"]'); this.meta = this.querySelector('[data-stream-meta]');
    if (!this.meta) return;
    this.messages = JSON.parse(this.meta.querySelector('[data-stream-messages]').textContent);
    this.status = this.meta.querySelector('[data-stream-status]');
    this.meta.querySelector('[data-stream-controls]').hidden = false;
    this.startPage = Number(this.meta.dataset.page); this.pages = Number(this.meta.dataset.pages); this.total = Number(this.meta.dataset.total);
    this.baseURL = new URL(window.location.href); this.key = streamKey(this.baseURL.href);
    this.nodes = this.grid ? [...this.grid.querySelectorAll(':scope > [ref="cards[]"]')] : [];
    this.ids = new Set(this.nodes.map(node => node.dataset.productId)); this.loaded = new Set([this.startPage]);
    if (!Number.isSafeInteger(this.pages) || this.pages < 0 || !Number.isSafeInteger(this.total) || this.total < 0 || !Number.isSafeInteger(this.startPage) || this.startPage < 1 || this.startPage > Math.max(1, this.pages) || (this.total > 0 && (!this.grid || this.pages < 1)) || this.ids.size !== this.nodes.length || this.nodes.some(node => !node.dataset.productId?.trim() || Number(node.dataset.page) !== this.startPage)) { this.fail('next', true); return; }
    this.setAttribute('data-enhanced', '');
    this.retryButton = this.meta.querySelector('[data-stream-retry]'); this.retryButton.hidden = true;
    this.meta.querySelector('[data-stream-fallback]').hidden = true;
    const saved = history.state?.size24Stream;
    this.restore = saved?.key === this.key && Number.isSafeInteger(saved.min) && Number.isSafeInteger(saved.max) ? { min: Math.max(1, saved.min), max: Math.min(this.pages, saved.max), scroll: saved.scroll } : null;
    this.top = this.querySelector('[data-stream-top]'); this.bottom = this.querySelector('[data-stream-bottom]');
    if (typeof IntersectionObserver === 'function') {
      this.intersections = new IntersectionObserver(() => this.pump(), { rootMargin: '600px' });
      if (this.top) this.intersections.observe(this.top); if (this.bottom) this.intersections.observe(this.bottom);
    }
    this.updateStatus(); this.pump();
  }
  async filterUpdate(event) {
    if (event.target.closest('s24-collection-stream') !== this) return;
    this.cancel(); this.filterPending = true;
    const generation = this.generation;
    try {
      if (!event.promise) throw new Error('Missing filter completion');
      await event.promise;
      if (this.isConnected && generation === this.generation) this.reset();
    } catch {
      if (this.isConnected && generation === this.generation) { this.filterPending = false; this.fail('next', true); }
    }
  }
  nearby(node) {
    if (!node) return false;
    const rect = node.getBoundingClientRect();
    return rect.top <= window.innerHeight + 600 && rect.bottom >= -600;
  }
  pump() {
    if (!this.isConnected || this.busy || this.error || this.filterPending || !this.grid || !this.total) return;
    if (this.restore) {
      if (Math.min(...this.loaded) > this.restore.min) { this.load('previous'); return; }
      if (Math.max(...this.loaded) < this.restore.max) { this.load('next'); return; }
      const scroll = this.restore.scroll; this.restore = null;
      if (Number.isFinite(scroll)) window.scrollTo({ top: scroll, behavior: 'instant' });
    }
    if (nextStreamPage(this.loaded, this.pages, 'previous') && this.nearby(this.top)) this.load('previous');
    else if (nextStreamPage(this.loaded, this.pages, 'next') && this.nearby(this.bottom)) this.load('next');
  }
  pageURL(page) {
    const url = new URL(this.baseURL.href); url.searchParams.set('page', String(page)); url.hash = '';
    url.searchParams.set('section_id', this.dataset.sectionId); return url;
  }
  async load(direction) {
    if (this.busy || this.filterPending || this.error) return;
    const page = nextStreamPage(this.loaded, this.pages, direction); if (!page) return;
    const generation = this.generation; const key = this.key;
    this.busy = true; this.request = new AbortController(); const request = this.request;
    this.status.textContent = this.messages.loading; this.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(this.pageURL(page), { signal: request.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Page unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const root = [...doc.querySelectorAll('s24-collection-stream')].find(node => node.dataset.sectionId === this.dataset.sectionId && node.dataset.collectionId === this.dataset.collectionId);
      const meta = root?.querySelector('[data-stream-meta]');
      if (!meta || Number(meta.dataset.page) !== page || Number(meta.dataset.pages) !== this.pages || Number(meta.dataset.total) !== this.total) throw new Error('Catalogue changed');
      const batch = [...root.querySelectorAll('[ref="grid"] > [ref="cards[]"]')]; const batchIds = new Set();
      if (!batch.length) throw new Error('Incomplete page');
      for (const node of batch) {
        const id = node.dataset.productId;
        if (!id || this.ids.has(id) || batchIds.has(id) || Number(node.dataset.page) !== page) throw new Error('Duplicate or unexpected product');
        batchIds.add(id);
      }
      if (!this.isConnected || generation !== this.generation || key !== streamKey(window.location.href) || request.signal.aborted) return;
      if (this.loaded.has(page)) return;
      if (this.ids.size + batch.length > this.total) throw new Error('Catalogue count changed');
      namespaceIncomingCards(batch, this.dataset.sectionId, this.ownerDocument);
      const first = this.grid.firstElementChild; const before = direction === 'previous' && first ? first.getBoundingClientRect().top : null;
      if (direction === 'previous') this.grid.prepend(...batch); else this.grid.append(...batch);
      for (const id of batchIds) this.ids.add(id); this.loaded.add(page);
      this.nodes = [...this.grid.querySelectorAll(':scope > [ref="cards[]"]')];
      if (before != null && first) window.scrollBy({ top: first.getBoundingClientRect().top - before, behavior: 'instant' });
      this.remember(false); this.updateStatus();
    } catch (error) {
      if (!request.signal.aborted && this.isConnected && generation === this.generation) this.fail(direction, false);
    } finally {
      if (generation === this.generation) {
        this.busy = false; this.request = null; this.removeAttribute('aria-busy');
        if (!this.error) this.updateStatus();
        requestAnimationFrame(() => this.pump());
      }
    }
  }
  updateStatus() {
    const complete = this.ids.size === this.total;
    this.status.textContent = (complete ? this.messages.complete : this.messages.progress).replace('__LOADED__', String(this.ids.size)).replace('__TOTAL__', String(this.total));
    if (this.loaded.size === this.pages && !complete) this.fail('next', true);
  }
  fail(direction, context) {
    this.error = { direction, context }; this.restore = null;
    if (!this.meta || !this.status) return;
    this.status.textContent = this.messages.error;
    this.meta.querySelector('[data-stream-retry]').hidden = false;
    const link = this.meta.querySelector('[data-stream-fallback]');
    const page = this.loaded && nextStreamPage(this.loaded, this.pages, direction);
    const url = context || !page ? new URL(window.location.href) : this.pageURL(page);
    url.searchParams.delete('section_id'); link.href = url.href; link.hidden = false;
  }
  retry() {
    if (!this.error || this.busy) return;
    if (this.error.context) { window.location.reload(); return; }
    const direction = this.error.direction; this.error = null;
    this.meta.querySelector('[data-stream-retry]').hidden = true; this.meta.querySelector('[data-stream-fallback]').hidden = true;
    this.load(direction);
  }
  remember(scroll) {
    if (!this.loaded?.size || this.filterPending || this.key !== streamKey(window.location.href)) return;
    const previous = history.state && typeof history.state === 'object' ? history.state : {};
    history.replaceState({ ...previous, size24Stream: { key: this.key, min: Math.min(...this.loaded), max: Math.max(...this.loaded), ...(scroll ? { scroll: window.scrollY } : {}) } }, '', window.location.href);
  }
}
if (!customElements.get('s24-collection-stream')) customElements.define('s24-collection-stream', CollectionStream);
